import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingScreen } from "../ui/LoadingScreen";
import { useAuth } from "../../hooks/useAuth";

interface OrgOnboardingRouteProps {
  children: React.ReactNode;
}

// Maximum time to wait for auth before showing content
const AUTH_TIMEOUT_MS = 8000;

export function OrgOnboardingRoute({ children }: OrgOnboardingRouteProps) {
  const [ready, setReady] = useState(false);
  const [authTimeout, setAuthTimeout] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // CRITICAL: Timeout to prevent infinite loading
  useEffect(() => {
    if (authLoading) {
      const timeout = setTimeout(() => {
        console.warn('[OrgOnboardingRoute] ⚠️ Auth timeout - showing onboarding anyway');
        setAuthTimeout(true);
        setReady(true);
      }, AUTH_TIMEOUT_MS);
      
      return () => clearTimeout(timeout);
    }
  }, [authLoading]);

  useEffect(() => {
    // Skip if already timed out
    if (authTimeout) return;
    
    // CRITICAL: Wait for auth to fully initialize
    if (authLoading) {
      console.log('[OrgOnboardingRoute] Waiting for auth...', { authLoading });
      return;
    }
    
    // Auth loaded - check profile status from centralized context
    if (user?.profileComplete === true) {
      console.log('[OrgOnboardingRoute] ✅ Profile complete → /home');
      navigate('/home', { replace: true });
      return;
    }
    
    // Profile incomplete or no user - allow onboarding
    console.log('[OrgOnboardingRoute] Profile incomplete → show onboarding');
    setReady(true);
  }, [user, authLoading, navigate, authTimeout]);

  if (!ready) {
    return <LoadingScreen message="Förbereder registrering..." />;
  }

  return <>{children}</>;
}
